import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

const PRESET_COLORS = [
  { hex: "#f97316", label: "Orange" },
  { hex: "#ef4444", label: "Red" },
  { hex: "#1d4ed8", label: "Blue" },
  { hex: "#16a34a", label: "Green" },
  { hex: "#9333ea", label: "Purple" },
  { hex: "#eab308", label: "Yellow" },
]

// Connects to data-controller="block-map-picker"
export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "pinColor", "colorSwatch"]

  static values = {
    token:         String,
    siteLatitude:  { type: Number, default: 51.505 },
    siteLongitude: { type: Number, default: -0.09 },
    blockLatitude: { type: Number, default: 0 },
    blockLongitude: { type: Number, default: 0 },
    color:         { type: String, default: "#f97316" },
  }

  connect() {
    if (!this.tokenValue) {
      console.error("block-map-picker: Mapbox token is required")
      return
    }

    mapboxgl.accessToken = this.tokenValue

    const centerLat = this.siteLatitudeValue
    const centerLng = this.siteLongitudeValue

    this.map = new mapboxgl.Map({
      container:   this.mapTarget,
      style:       MAP_STYLE,
      config:      MAP_CONFIG,
      center:      [centerLng, centerLat],
      zoom:        15,
      interactive: true,
    })

    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // If the block already has coordinates, place the marker there
    const hasExisting = this.blockLatitudeValue !== 0 || this.blockLongitudeValue !== 0
    const markerLat = hasExisting ? this.blockLatitudeValue : centerLat
    const markerLng = hasExisting ? this.blockLongitudeValue : centerLng

    this.selectedColor = this.colorValue

    this.marker = new mapboxgl.Marker({ draggable: true, color: this.selectedColor })
      .setLngLat([markerLng, markerLat])
      .addTo(this.map)

    this.marker.on("dragend", () => this.syncCoordinates())

    this.map.on("click", (e) => {
      this.marker.setLngLat(e.lngLat)
      this.syncCoordinates()
    })

    this.syncCoordinates()
    this.updateSwatchSelection()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  selectColor(event) {
    const swatch = event.currentTarget
    const color = swatch.dataset.color

    this.selectedColor = color
    this.pinColorTarget.value = color.replace("#", "")

    // Rebuild marker with new color (Mapbox GL doesn't support color change in place)
    const currentLngLat = this.marker.getLngLat()
    this.marker.remove()
    this.marker = new mapboxgl.Marker({ draggable: true, color: this.selectedColor })
      .setLngLat(currentLngLat)
      .addTo(this.map)
    this.marker.on("dragend", () => this.syncCoordinates())

    this.updateSwatchSelection()
  }

  syncCoordinates() {
    const { lat, lng } = this.marker.getLngLat()
    this.latitudeTarget.value  = lat
    this.longitudeTarget.value = lng
  }

  updateSwatchSelection() {
    this.colorSwatchTargets.forEach((swatch) => {
      const isSelected = swatch.dataset.color === this.selectedColor
      swatch.classList.toggle("ring-2", isSelected)
      swatch.classList.toggle("ring-offset-2", isSelected)
      swatch.classList.toggle("ring-gray-900", isSelected)
    })
  }
}
